// web/src/components/chat/useAnalysis.ts
import { useEffect } from 'react'
import { api } from '../../api/http'
import { rpc } from '../../api/rpc'

const SANDBOX_URL = '/analysis-sandbox.html'
// Stays under the WS frame limit (2 MB); the server caps what reaches the model anyway.
const MAX_RESULT_CHARS = 200_000
const MAX_ERROR_CHARS = 20_000

interface AnalysisRequestEvent { chatId: string; requestId: string; code: string; files: string[]; timeoutMs: number }
interface AnalysisRefEvent { chatId: string; requestId: string }
type Outcome = { output: string } | { error: string }

/**
 * Runs one analysis in a fresh `sandbox="allow-scripts"` iframe (opaque origin, no cookies,
 * no network by the page's CSP) and resolves with its result.
 */
export function runInSandbox(code: string, input: unknown, timeoutMs: number, signal: AbortSignal): Promise<Outcome> {
  return new Promise(resolve => {
    const frame = document.createElement('iframe')
    frame.setAttribute('sandbox', 'allow-scripts')
    frame.setAttribute('aria-hidden', 'true')
    frame.style.display = 'none'
    frame.src = SANDBOX_URL
    let settled = false
    const finish = (r: Outcome): void => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      signal.removeEventListener('abort', onAbort)
      frame.contentWindow?.postMessage({ type: 'analysis-cancel' }, '*')
      frame.remove()
      resolve(r)
    }
    const onMessage = (e: MessageEvent): void => {
      if (e.source !== frame.contentWindow) return
      const d = e.data as { type?: string; output?: unknown; error?: unknown } | null
      if (d?.type === 'analysis-ready') frame.contentWindow?.postMessage({ type: 'analysis-run', code, input, timeoutMs }, '*')
      else if (d?.type === 'analysis-result') {
        finish(typeof d.error === 'string' ? { error: d.error } : { output: typeof d.output === 'string' ? d.output : '' })
      }
    }
    const onAbort = (): void => finish({ error: 'aborted' })
    // The page enforces timeoutMs itself; this only covers a page that never loads.
    const timer = setTimeout(() => finish({ error: 'analysis timed out' }), timeoutMs + 5_000)
    window.addEventListener('message', onMessage)
    signal.addEventListener('abort', onAbort, { once: true })
    document.body.appendChild(frame)
  })
}

/** Answers `analysis.request` events of the open chat. Another tab may claim first (409): then this one does nothing. */
export function useAnalysis(chatId: string | undefined): void {
  useEffect(() => {
    if (!chatId) return
    const running = new Map<string, AbortController>()
    const offRequest = rpc.on('analysis.request', (p: AnalysisRequestEvent) => {
      if (p.chatId !== chatId || running.has(p.requestId)) return
      const ctl = new AbortController()
      running.set(p.requestId, ctl)
      void (async () => {
        let input: unknown
        try {
          input = await api.get<unknown>(`/api/analysis/${encodeURIComponent(p.requestId)}/input`)
        } catch {
          running.delete(p.requestId)
          return
        }
        const r = await runInSandbox(p.code, input, p.timeoutMs, ctl.signal)
        running.delete(p.requestId)
        if (ctl.signal.aborted) return
        const payload = 'error' in r
          ? { requestId: p.requestId, error: r.error.slice(0, MAX_ERROR_CHARS) }
          : { requestId: p.requestId, output: r.output.slice(0, MAX_RESULT_CHARS) }
        await rpc.call('chats.analysisResult', payload).catch(() => undefined)
      })()
    })
    const stop = (p: AnalysisRefEvent): void => {
      if (p.chatId !== chatId) return
      running.get(p.requestId)?.abort()
      running.delete(p.requestId)
    }
    const offCancel = rpc.on('analysis.cancel', stop)
    const offSettled = rpc.on('analysis.settled', stop)
    return () => {
      offRequest(); offCancel(); offSettled()
      for (const c of running.values()) c.abort()
      running.clear()
    }
  }, [chatId])
}
